import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useActiveTenantId } from '@/store/tenantStore'
import {
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  useToast,
} from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import { employeesApi, type Employee } from '../api/employeesApi'
import { organizationsApi, type Organization } from '@/modules/system/organizations/api/organizationsApi'
import { departmentsApi, type Department } from '@/modules/organization/departments/api/departmentsApi'

type EmployeeForm = {
  employeeNumber: string
  name: string
  surname: string
  email: string
  phone: string
  hireDate: string
  organizationId: string
  departmentId: string
  isActive: boolean
}

function emptyToNull(s: string): string | null {
  const t = s?.trim()
  return t ? t : null
}

function toDateInput(value?: string | null): string {
  if (!value) return ''
  return value.length >= 10 ? value.slice(0, 10) : value
}

export default function EmployeesPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const activeTenantId = useActiveTenantId()
  const tenantIdFromUrl = searchParams.get('tenantId')
  const tenantId = tenantIdFromUrl ? Number(tenantIdFromUrl) : activeTenantId
  const { toast } = useToast()

  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [lookupsLoading, setLookupsLoading] = useState(false)

  const loadLookups = useCallback(async () => {
    if (!tenantId) return
    setLookupsLoading(true)
    try {
      const [orgRes, deptRes] = await Promise.all([
        organizationsApi.list({ tenantId, pageSize: 500, isActive: true }),
        departmentsApi.list({ tenantId, pageSize: 500, includeDeleted: false }),
      ])
      setOrganizations(orgRes.items)
      setDepartments(deptRes.items)
    } catch {
      setOrganizations([])
      setDepartments([])
    } finally {
      setLookupsLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    loadLookups()
  }, [loadLookups])

  const orgOptions = organizations.map((o) => ({
    value: String(o.organizationId),
    label: `${o.code ?? ''} - ${o.name}`.trim() || o.name,
  }))

  const getDepartmentOptions = (organizationId?: string) => {
    const selectedOrgId = organizationId ? Number(organizationId) : null
    return departments
      .filter((d) => !selectedOrgId || d.organizationId === selectedOrgId)
      .map((d) => ({
        value: String(d.departmentId),
        label: `${d.code ?? ''} - ${d.name}`.trim() || d.name,
      }))
  }

  const departmentNameById = useMemo(() => {
    const map = new Map<number, string>()
    departments.forEach((d) => map.set(d.departmentId, d.name))
    return map
  }, [departments])

  const organizationNameById = useMemo(() => {
    const map = new Map<number, string>()
    organizations.forEach((o) => map.set(o.organizationId, o.name))
    return map
  }, [organizations])

  const openEmployeeEdit = useCallback((employee: Employee) => {
    const query = tenantId != null ? `?tenantId=${tenantId}` : ''
    navigate(`/employee/${employee.employeeId}/edit${query}`, {
      state: { employeeFromList: employee },
    })
  }, [navigate, tenantId])

  return (
    <CrudPage<Employee, EmployeeForm, any, any>
      title="Çalışanlar"
      subtitle="HR çalışan kayıtlarını yönet"
      primaryActionLabel="Yeni Çalışan"
      onPrimaryActionClick={() => navigate('/employee/new')}
      rowClickMode="none"
      onEditActionClick={openEmployeeEdit}
      onExportClick={() =>
        toast({
          title: 'Eksik',
          description: "Backend'de export endpoint'i yok. TODO: GET /api/v1/employees/export (Reporting).",
          variant: 'warning',
        })
      }
      onImportClick={() =>
        toast({
          title: 'Eksik',
          description: "Backend'de import endpoint'i yok. TODO: POST /api/v1/employees/import (HR write).",
          variant: 'warning',
        })
      }
      list={async (p) =>
        employeesApi.list({
          tenantId,
          search: p.search,
          page: p.page,
          pageSize: p.pageSize,
          sortBy: (p.sortBy as any) ?? undefined,
          sortDir: (p.sortDir as any) ?? undefined,
          isActive: p.filters.status === 'all' ? null : p.filters.status === 'active',
          includeDeleted: p.filters.deleted === 'show',
        })
      }
      getRowId={(e) => e.employeeId}
      columns={[
        {
          key: 'employeeNumber',
          header: 'Sicil No',
          sortable: true,
          widthClassName: 'w-[130px]',
          render: (e) => <span className="font-medium">{e.employeeNumber ?? '-'}</span>,
        },
        {
          key: 'name',
          header: 'Ad Soyad',
          sortable: true,
          widthClassName: 'w-[220px]',
          render: (e) => `${e.name} ${e.surname ?? ''}`.trim(),
        },
        {
          key: 'email',
          header: 'E-posta',
          sortable: false,
          render: (e) => e.email ?? '-',
        },
        {
          key: 'organizationId',
          header: 'Organizasyon',
          sortable: false,
          render: (e) => (e.organizationId ? organizationNameById.get(e.organizationId) ?? String(e.organizationId) : '-'),
        },
        {
          key: 'departmentId',
          header: 'Departman',
          sortable: false,
          render: (e) => (e.departmentId ? departmentNameById.get(e.departmentId) ?? String(e.departmentId) : '-'),
        },
        {
          key: 'isActive',
          header: 'Durum',
          sortable: false,
          widthClassName: 'w-[100px]',
          render: (e) => (e.isActive ? <Badge variant="success">Aktif</Badge> : <Badge variant="warning">Pasif</Badge>),
        },
      ]}
      searchPlaceholder="Ara (sicil no, ad, soyad, e-posta, telefon)..."
      filters={[
        {
          id: 'status',
          label: 'Durum',
          value: 'all',
          options: [
            { label: 'Tümü', value: 'all' },
            { label: 'Aktif', value: 'active' },
            { label: 'Pasif', value: 'inactive' },
          ],
        },
        {
          id: 'deleted',
          label: 'Silinenler',
          value: 'hide',
          options: [
            { label: 'Gizle', value: 'hide' },
            { label: 'Göster', value: 'show' },
          ],
          widthClassName: 'min-w-[160px]',
        },
      ]}
      initialPageSize={10}
      modalTitles={{ create: 'Yeni Çalışan', edit: 'Çalışanı Düzenle' }}
      getCreateDefaultForm={() => ({
        employeeNumber: '',
        name: '',
        surname: '',
        email: '',
        phone: '',
        hireDate: '',
        organizationId: '',
        departmentId: '',
        isActive: true,
      })}
      getEditForm={(e) => ({
        employeeNumber: e.employeeNumber ?? '',
        name: e.name ?? '',
        surname: e.surname ?? '',
        email: e.email ?? '',
        phone: e.phone ?? '',
        hireDate: toDateInput(e.hireDate),
        organizationId: e.organizationId != null ? String(e.organizationId) : '',
        departmentId: e.departmentId != null ? String(e.departmentId) : '',
        isActive: e.isActive,
      })}
      validate={(f) => ({
        employeeNumber: !f.employeeNumber.trim() ? 'Sicil no zorunludur' : undefined,
        name: !f.name.trim() ? 'Ad zorunludur' : undefined,
        surname: !f.surname.trim() ? 'Soyad zorunludur' : undefined,
        email: f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()) ? 'Geçerli e-posta giriniz' : undefined,
      })}
      renderForm={({ form, setForm, touched, setTouched, errors, isSubmitting }) => (
        <div className="grid gap-4">
          <Input
            label="Sicil No"
            value={form.employeeNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, employeeNumber: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.employeeNumber}
            helperText={touched ? errors.employeeNumber : undefined}
            required
            disabled={isSubmitting}
            placeholder="Örn: EMP-0001"
          />
          <Input
            label="Ad"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, name: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            required
            disabled={isSubmitting}
          />
          <Input
            label="Soyad"
            value={form.surname}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, surname: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.surname}
            helperText={touched ? errors.surname : undefined}
            required
            disabled={isSubmitting}
          />
          <Input
            label="E-posta"
            value={form.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, email: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.email}
            helperText={touched ? errors.email : undefined}
            disabled={isSubmitting}
          />
          <Input
            label="Telefon"
            value={form.phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, phone: e.target.value }))}
            disabled={isSubmitting}
          />
          <Input
            label="İşe Giriş Tarihi"
            type="date"
            value={form.hireDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, hireDate: e.target.value }))}
            disabled={isSubmitting}
          />
          <div>
            <label className="block text-sm font-medium mb-1.5">Organizasyon Birimi</label>
            <Select
              value={form.organizationId ? String(form.organizationId) : '__none__'}
              onValueChange={(v: string) =>
                setForm((s) => ({
                  ...s,
                  organizationId: v === '__none__' ? '' : v,
                  departmentId: '',
                }))
              }
              disabled={isSubmitting || lookupsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Organizasyon seçin (opsiyonel)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Seçin —</SelectItem>
                {orgOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Departman</label>
            <Select
              value={form.departmentId ? String(form.departmentId) : '__none__'}
              onValueChange={(v: string) => setForm((s) => ({ ...s, departmentId: v === '__none__' ? '' : v }))}
              disabled={isSubmitting || lookupsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Departman seçin (opsiyonel)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Seçin —</SelectItem>
                {getDepartmentOptions(form.organizationId).map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Switch
            checked={form.isActive}
            onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, isActive: v }))}
            label="Aktif"
            disabled={isSubmitting}
          />
        </div>
      )}
      buildCreatePayload={(f) => ({
        tenantId,
        employeeNumber: f.employeeNumber.trim(),
        name: f.name.trim(),
        surname: f.surname.trim(),
        email: emptyToNull(f.email),
        phone: emptyToNull(f.phone),
        hireDate: f.hireDate ? new Date(`${f.hireDate}T00:00:00`).toISOString() : null,
        organizationId: f.organizationId ? Number(f.organizationId) : null,
        departmentId: f.departmentId ? Number(f.departmentId) : null,
        isActive: f.isActive,
      })}
      buildUpdatePayload={(f) => ({
        tenantId,
        employeeNumber: f.employeeNumber.trim(),
        name: f.name.trim(),
        surname: f.surname.trim(),
        email: emptyToNull(f.email),
        phone: emptyToNull(f.phone),
        hireDate: f.hireDate ? new Date(`${f.hireDate}T00:00:00`).toISOString() : null,
        organizationId: f.organizationId ? Number(f.organizationId) : null,
        departmentId: f.departmentId ? Number(f.departmentId) : null,
        isActive: f.isActive,
      })}
      create={(payload) => employeesApi.create(payload)}
      update={(id, payload) => employeesApi.update({ id: Number(id), ...(payload as any) })}
      remove={(id) => employeesApi.delete(Number(id))}
      getCreatedId={(res) => res?.data?.id}
    />
  )
}
